<template>
  <v-dialog v-model="dialogOpen" max-width="720" scrollable persistent>
    <v-card v-if="isSuperadmin">
      <v-toolbar color="primary" title="Transferir de grupo">
        <v-btn icon variant="text" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>
      <p class="transfer-invoice-warning d-none">
        As faturas e o histórico financeiro permanecem no grupo de origem. Apenas o vínculo de associação muda.
      </p>
      <v-alert type="warning" variant="tonal" density="compact" class="mx-4 mb-0">
        As faturas e o histórico financeiro permanecem no grupo de origem. Apenas o vínculo de associação muda.
      </v-alert>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-text-field
                          :model-value="fromGroupLabel"
                          label="Grupo de origem"
                          readonly
                          disabled
                          density="compact"
                          hide-details></v-text-field>
          </v-col>
          <v-col cols="12">
            <AutocompleteSelectGroup
                                     :key="'to-group-' + (person && person.id) + '-' + String(modelValue)"
                                     @selected="onToGroupSelected"
                                     :selected="toGroup" />
          </v-col>
          <v-col cols="12">
            <v-switch
                      v-model="includeDependants"
                      label="Incluir dependentes"
                      density="compact"
                      hide-details
                      color="primary"></v-switch>
            <ul v-if="includeDependants" class="mt-2">
              <li v-for="dep in originDependants" :key="dep.id">{{ dep.name }}</li>
              <li v-if="!originDependants.length">Nenhum dependente no grupo de origem</li>
            </ul>
          </v-col>
          <v-col cols="12" v-if="toGroupId">
            <v-select
                      v-model="subGroupIds"
                      :items="destinationSubGroups"
                      item-title="name"
                      item-value="id"
                      label="Subgrupos de destino"
                      multiple
                      chips
                      clearable
                      density="compact"
                      hide-details></v-select>
          </v-col>
          <v-col cols="12">
            <v-select
                      v-model="role"
                      :items="roleItems"
                      item-title="title"
                      item-value="value"
                      label="Papel no destino"
                      density="compact"
                      hide-details></v-select>
          </v-col>
        </v-row>
        <div v-if="preview" class="mt-4">
          <p class="font-weight-medium">Prévia</p>
          <ul>
            <li v-for="row in preview.transferred || []" :key="row.legal_person_id">
              {{ row.name }}
            </li>
          </ul>
          <p>Faturas no grupo de origem: {{ preview.invoices_untouched?.origin_group_count }}</p>
          <p>Acessos de loja revogados: {{ (preview.stores_revoked || []).length }}</p>
        </div>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn variant="text" color="error" @click="close">Cancelar</v-btn>
        <v-spacer></v-spacer>
        <v-btn
               color="secondary"
               variant="text"
               :disabled="!canPreview || requestingApi"
               @click="runPreview">Prévia</v-btn>
        <v-btn
               color="success"
               variant="text"
               :disabled="!preview || requestingApi"
               @click="runConfirm">Confirmar</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import AutocompleteSelectGroup from './AutocompleteSelectGroup.vue'
import api from '@/services'

export default {
  name: 'DialogTransferGroup',
  components: { AutocompleteSelectGroup },
  props: {
    modelValue: Boolean,
    person: Object,
    user: Object,
  },
  emits: ['update:modelValue', 'transferred'],
  data() {
    return {
      toGroupId: null,
      toGroup: null,
      includeDependants: false,
      dependants: [],
      destinationSubGroups: [],
      subGroupIds: [],
      role: '',
      preview: null,
      requestingApi: false,
      roleItems: [
        { title: 'Manter papel atual', value: '' },
        { title: 'Membro', value: 0 },
        { title: 'Administrador do grupo', value: 7 },
      ],
    }
  },
  computed: {
    isSuperadmin() {
      return (this.user?.access_level || 0) >= 777
    },
    dialogOpen: {
      get() {
        return !!this.modelValue && this.isSuperadmin
      },
      set(value) {
        this.$emit('update:modelValue', value)
      },
    },
    fromGroupId() {
      return this.person?.group_id ?? this.person?.group?.id ?? null
    },
    fromGroupLabel() {
      const group = this.person?.group
      const id = this.fromGroupId
      if (group) {
        const name = group.name || group.acronym || ''
        return name ? `${name} [${id}]` : String(id ?? '')
      }
      return id != null ? String(id) : ''
    },
    originDependants() {
      const fromId = this.fromGroupId
      return (this.dependants || []).filter((d) => d.group_id === fromId)
    },
    canPreview() {
      return !!(this.person?.id && this.fromGroupId && this.toGroupId && this.toGroupId !== this.fromGroupId)
    },
  },
  watch: {
    modelValue(open) {
      if (open && !this.isSuperadmin) {
        this.close()
        return
      }
      if (open) {
        this.resetForm()
        this.loadPersonDetails()
      }
    },
    person: {
      handler() {
        if (this.modelValue) {
          this.resetForm()
          this.loadPersonDetails()
        }
      },
    },
    toGroupId() {
      this.preview = null
      this.loadDestinationSubGroups()
    },
    includeDependants() {
      this.preview = null
    },
    subGroupIds: {
      handler() {
        this.preview = null
      },
      deep: true,
    },
    role() {
      this.preview = null
    },
  },
  mounted() {
    if (this.modelValue && !this.isSuperadmin) {
      this.close()
      return
    }
    if (this.modelValue) {
      this.loadPersonDetails()
    }
  },
  methods: {
    close() {
      this.dialogOpen = false
    },
    resetForm() {
      this.toGroupId = null
      this.toGroup = null
      this.includeDependants = false
      this.destinationSubGroups = []
      this.subGroupIds = []
      this.role = ''
      this.preview = null
      this.requestingApi = false
    },
    onToGroupSelected(group) {
      this.toGroup = group || null
      this.toGroupId = group?.id || null
    },
    dependantsFromPerson(source) {
      const list = source?.dependants || source?.children || []
      return Array.isArray(list) ? list : []
    },
    async loadPersonDetails() {
      const person = this.person
      if (!person?.id) {
        this.dependants = []
        return
      }
      const local = this.dependantsFromPerson(person)
      if (local.length) {
        this.dependants = local
        return
      }
      try {
        const full = await api.legalPerson.get(person.id)
        this.dependants = this.dependantsFromPerson(full)
      } catch (err) {
        this.dependants = []
        if (err?.message) {
          this.$notify({ type: 'warn', text: err.message })
        }
      }
    },
    async loadDestinationSubGroups() {
      this.subGroupIds = []
      this.destinationSubGroups = []
      if (!this.toGroupId) return
      try {
        const result = await api.subGroup.list(
          { selected_group_id: this.toGroupId, itemsPerPage: 200 },
          1
        )
        this.destinationSubGroups = result?.data || []
      } catch (err) {
        this.destinationSubGroups = []
        if (err?.message) {
          this.$notify({ type: 'warn', text: err.message })
        }
      }
    },
    payloadRole() {
      const role = this.role
      if (role === 0 || role === '0') return 0
      if (role === 7 || role === '7') return 7
      return null
    },
    buildPayload(dryRun) {
      return {
        legal_person_id: this.person.id,
        from_group_id: this.fromGroupId,
        to_group_id: this.toGroupId,
        include_dependants: !!this.includeDependants,
        sub_group_ids: this.subGroupIds || [],
        role: this.payloadRole(),
        revoke_store_access: true,
        dry_run: dryRun,
      }
    },
    errorMessage(err) {
      return err?.response?.data?.message || err?.message || 'Erro ao transferir'
    },
    async runPreview() {
      if (!this.canPreview || this.requestingApi) return
      this.requestingApi = true
      try {
        this.preview = await api.legalPerson.transferGroup(this.buildPayload(true))
      } catch (err) {
        this.preview = null
        this.$notify({ type: 'warn', text: this.errorMessage(err) })
      } finally {
        this.requestingApi = false
      }
    },
    async runConfirm() {
      if (!this.preview || this.requestingApi) return
      this.requestingApi = true
      try {
        const result = await api.legalPerson.transferGroup(this.buildPayload(false))
        const transferredIds = (result?.transferred || []).map((row) => row.legal_person_id)
        const meLpId = api.auth.user()?.legal_person?.id
        if (meLpId && transferredIds.includes(meLpId)) {
          await api.auth.me()
          const toId = result?.to_group_id || this.toGroupId
          let group = this.toGroup?.id === toId ? this.toGroup : null
          if (!group && api.group?.get) {
            try {
              group = await api.group.get(toId)
            } catch (_) {
              group = null
            }
          }
          api.storage.selectGroup(group || { id: toId })
        }
        this.$emit('transferred', result)
        this.$notify({
          type: 'success',
          text: 'Transferência concluída',
        })
        this.close()
      } catch (err) {
        this.$notify({ type: 'warn', text: this.errorMessage(err) })
      } finally {
        this.requestingApi = false
      }
    },
  },
}
</script>
